import React, { useState } from "react";
import { Share2, Link2, MessageCircle, Send, Facebook, Instagram } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

// Product share menu — WhatsApp / Viber / Facebook work everywhere;
// Instagram & Stories go through the phone's native share sheet (mobile),
// with copy-link as the desktop fallback.
export const ShareMenu = ({ url, title, triggerClassName = "", iconOnly = false, testid = "share" }) => {
  const { t } = useLang();
  const s = t.share;
  const [open, setOpen] = useState(false);

  const encoded = encodeURIComponent(url);
  const textEncoded = encodeURIComponent(`${title}\n${url}`);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(s.copied);
    } catch {
      window.prompt(url, url);
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: title, url });
        setOpen(false);
      } catch { /* user dismissed */ }
    } else {
      await copyLink();
    }
  };

  const Option = ({ href, onClick, icon: Icon, label, testid: tid }) => (
    <a
      href={href || "#"}
      target={href ? "_blank" : undefined}
      rel="noreferrer"
      onClick={onClick ? (e) => { e.preventDefault(); onClick(); } : undefined}
      className="flex items-center gap-3 rounded-2xl border border-brand/15 bg-white px-4 py-3.5 font-bold text-brand-ink hover:bg-brand-soft/50 transition-colors no-a11y-underline"
      data-testid={tid}
    >
      <Icon className="h-5 w-5 text-brand shrink-0" /> {label}
    </a>
  );

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={triggerClassName}
        data-testid={`${testid}-open`}
        aria-label={s.button}
      >
        <Share2 className="h-4 w-4" /> {!iconOnly && s.button}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-brand-cream border-brand/15 max-w-sm rounded-[1.75rem]" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-brand-ink">{s.title}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2.5">
            <Option href={`https://wa.me/?text=${textEncoded}`} icon={MessageCircle} label={s.whatsapp} testid={`${testid}-whatsapp`} />
            <Option href={`viber://forward?text=${textEncoded}`} icon={Send} label={s.viber} testid={`${testid}-viber`} />
            <Option href={`https://www.facebook.com/sharer/sharer.php?u=${encoded}`} icon={Facebook} label={s.facebook} testid={`${testid}-facebook`} />
            <Option onClick={nativeShare} icon={Instagram} label={s.instagram} testid={`${testid}-instagram`} />
            <Option onClick={copyLink} icon={Link2} label={s.copy} testid={`${testid}-copy`} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
